import type { ApiResult } from "../http.js";
import type { HttpClient } from "../http.js";
import type {
  AddInvoiceLineItemPayload,
  ChangeInvoiceStatusPayload,
  CreateInvoicePayload,
  NetInvoiceDto,
  PagedInvoices,
  PaymentDto,
  SendMethod,
} from "../types/invoices.js";

export interface RegisterPaymentPayload {
  amount: number;
  currency: string;
  /** YYYY-MM-DD */
  paidOn: string;
}

export interface ListInvoicesPagedQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  projectId?: string;
}

export class InvoicesApi {
  constructor(private readonly http: HttpClient) {}

  get(id: string): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.get<NetInvoiceDto>(`/api/invoices/${encodeURIComponent(id)}`);
  }

  /**
   * Lists invoices in the active org, newest issue date first.
   * Corresponds to GET /api/invoices. Pass `projectId` to get only that project's
   * invoices — the server filters in SQL, so project screens do not pull the whole org.
   */
  list(options?: { projectId?: string }): Promise<ApiResult<NetInvoiceDto[]>> {
    const path = options?.projectId
      ? `/api/invoices?projectId=${encodeURIComponent(options.projectId)}`
      : "/api/invoices";
    return this.http.get<NetInvoiceDto[]>(path);
  }

  /**
   * Server-paged, server-searched sibling of {@link list}. Corresponds to
   * GET /api/invoices/paged. `search` matches invoice number, e-conomic booked number or customer name.
   */
  listPaged(query: ListInvoicesPagedQuery = {}): Promise<ApiResult<PagedInvoices>> {
    return this.http.get<PagedInvoices>("/api/invoices/paged", { query: { ...query } });
  }

  /**
   * Creates a Draft invoice. Returns the new invoice's id (a raw Guid string).
   * Corresponds to POST /api/invoices.
   */
  create(payload: CreateInvoicePayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/invoices", { body: payload });
  }

  /**
   * Appends a line item to a Draft invoice.
   * Corresponds to POST /api/invoices/{id}/line-items.
   */
  addLineItem(
    invoiceId: string,
    payload: AddInvoiceLineItemPayload,
  ): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.post<NetInvoiceDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/line-items`,
      { body: payload },
    );
  }

  /**
   * Registers a payment against a .NET invoice. Valid only when the invoice
   * is in Booked, Sent, or Overdue status. Returns the newly created payment.
   */
  registerPayment(
    invoiceId: string,
    payload: RegisterPaymentPayload,
  ): Promise<ApiResult<PaymentDto>> {
    return this.http.post<PaymentDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/payments`,
      { body: payload },
    );
  }

  /**
   * Drives an invoice through its lifecycle (issue #819): Draft → Booked → Sent → Paid,
   * plus credit notes and write-offs. The .NET state machine rejects invalid transitions.
   * Corresponds to POST /api/invoices/{id}/status.
   */
  changeStatus(
    invoiceId: string,
    payload: ChangeInvoiceStatusPayload,
  ): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.post<NetInvoiceDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/status`,
      { body: payload },
    );
  }

  /**
   * Cancels a draft invoice (issue #819). Only valid while the invoice is in Draft —
   * a booked invoice must be corrected with a credit note instead.
   * Corresponds to POST /api/invoices/{id}/cancel.
   */
  cancel(invoiceId: string): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.post<NetInvoiceDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/cancel`,
      {},
    );
  }

  /**
   * Re-runs the e-conomic push for an invoice that is booked in Mesterplan but never
   * reached e-conomic (no `economicBookedNumber`). Safe to repeat: an existing draft is
   * reused rather than duplicated. Returns 409 carrying the reason when the push is still
   * blocked — e.g. no default invoice product configured, or the customer is not synced.
   * Corresponds to POST /api/invoices/{id}/economic/push.
   */
  pushToEconomic(invoiceId: string): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.post<NetInvoiceDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/economic/push`,
    );
  }

  /**
   * Sends a booked invoice via e-conomic (delivery to the customer on the invoice).
   * The endpoint REQUIRES a body of shape { method } — omitting it is a 400.
   * Corresponds to POST /api/invoices/{id}/send-via-economic.
   */
  sendViaEconomic(
    invoiceId: string,
    method: SendMethod = "Email",
  ): Promise<ApiResult<NetInvoiceDto>> {
    return this.http.post<NetInvoiceDto>(
      `/api/invoices/${encodeURIComponent(invoiceId)}/send-via-economic`,
      { body: { method } },
    );
  }

  /**
   * Downloads the PDF for a booked invoice as an ArrayBuffer.
   * Corresponds to GET /api/invoices/{id}/pdf.
   * Goes through the full http pipeline (base-url + auth + error handling);
   * web callers wrap the buffer in a Blob and create an object URL.
   */
  getPdf(invoiceId: string): Promise<ApiResult<ArrayBuffer>> {
    return this.http.getBinary(
      `/api/invoices/${encodeURIComponent(invoiceId)}/pdf`,
    );
  }
}
